import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPopup.css";
import { useSignIn, useSignUp, useUser } from "@clerk/clerk-react";
import { useNav } from "../../Context/NavContext";
import axios from "axios";

export default function LoginPopup({ onClose, onLoginSuccess }) {
  const navigate = useNavigate();
  const { login } = useNav();
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();

  // Handle Google auth callback
  useEffect(() => {
    const handleAuthCallback = async () => {
      if (userLoaded && isSignedIn && user) {
        try {
          const firstName = user.firstName || "";
          const lastName = user.lastName || "";
          const fullName = `${firstName} ${lastName}`.trim();

          const userData = {
            name: fullName || user.primaryEmailAddress?.emailAddress || "User",
            email: user.primaryEmailAddress?.emailAddress || "",
            isLoggedIn: true,
            profileImage: user.imageUrl,
            provider: user.externalAccounts.length > 0
              ? user.externalAccounts[0].provider.toUpperCase()
              : "EMAIL",
          };

          login(userData);

          if (onLoginSuccess) {
            onLoginSuccess(userData);
          } else {
            onClose();
            navigate("/userprofile");
          }
        } catch (error) {
          console.error("Error handling Google auth callback:", error);
          setError("Failed to complete Google authentication");
        }
      }
    };

    handleAuthCallback();
  }, [userLoaded, isSignedIn, user, login, onLoginSuccess, onClose, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
    if (needsVerification) setNeedsVerification(false);
  };

  const handleApiAuth = async (isLogin) => {
    try {
      const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";
      const endpoint = isLogin
        ? `${baseURL}/api/users/login`
        : `${baseURL}/api/users/register`;

      const response = await axios.post(endpoint, {
        name: !isLogin ? formData.name : undefined,
        email: formData.email,
        password: formData.password,
      });

      if (response.data.needsVerification) {
        setNeedsVerification(true);
        setError(isLogin
          ? "Please verify your email before logging in."
          : "Registration successful. Please verify your email.");
        return false;
      }

      const { token, name } = response.data;

      const userInfo = {
        name,
        email: formData.email,
        isLoggedIn: true,
        token,
        provider: 'API',
      };

      login(userInfo);

      if (onLoginSuccess) {
        onLoginSuccess(userInfo);
      } else {
        onClose();
        navigate("/userprofile");
      }

      return true;
    } catch (error) {
      console.error('API Auth Error:', error);
      if (error.response?.data?.needsVerification) {
        setNeedsVerification(true);
        setError(isLogin
          ? "Please verify your email before logging in."
          : "Registration successful. Please verify your email.");
        return false;
      }
      throw new Error(error.response?.data?.message || "Authentication failed");
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError("Please enter your email address first.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";
      await axios.post(`${baseURL}/api/users/resend-verification`, {
        email: formData.email,
      });

      setError("Verification email resent! Check your inbox.");
    } catch (error) {
      console.error("Resend verification error:", error);
      setError(error.response?.data?.message || "Failed to resend verification email.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSuccess = (firstName, lastName) => {
    const name = `${firstName} ${lastName}`.trim();
    const userInfo = {
      name: name || formData.email,
      isLoggedIn: true,
      email: formData.email,
      provider: 'CLERK',
    };

    login(userInfo);

    if (onLoginSuccess) {
      onLoginSuccess(userInfo);
    } else {
      onClose();
      navigate("/userprofile");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Try API authentication first
      try {
        const success = await handleApiAuth(isLogin);
        if (success) return;
      } catch (apiError) {
        console.log('Falling back to Clerk authentication');
      }

      // Clerk Authentication as fallback
      if (isLogin) {
        if (!signInLoaded) {
          setError("Authentication service not ready. Please try again.");
          setIsLoading(false);
          return;
        }

        const result = await signIn.create({
          identifier: formData.email,
          password: formData.password,
        });

        if (result.status === "complete") {
          const userInfo = {
            name: formData.email,
            email: formData.email,
            isLoggedIn: true,
            provider: 'CLERK',
          };

          login(userInfo);

          if (onLoginSuccess) {
            onLoginSuccess(userInfo);
          } else {
            onClose();
            navigate("/userprofile");
          }
        } else if (result.status === "needs_verification") {
          setError("Please check your email for verification instructions.");
        } else {
          setError("Login incomplete. Please try again.");
        }
      } else {
        if (!signUpLoaded) {
          setError("Registration service not ready. Please try again.");
          setIsLoading(false);
          return;
        }

        const nameParts = formData.name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const result = await signUp.create({
          emailAddress: formData.email,
          password: formData.password,
          firstName,
          lastName,
        });

        if (result.status === "complete") {
          handleSignUpSuccess(firstName, lastName);
        } else if (result.status === "missing_requirements") {
          if (result.unverifiedFields.includes("email_address")) {
            setError("Please check your email to verify your account.");
          } else {
            setError("Additional information required. Please complete all fields.");
          }
        } else {
          setError("Registration incomplete. Please try again.");
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      let errorMessage = "Authentication failed. Please try again.";

      if (err.errors && err.errors.length > 0) {
        const firstError = err.errors[0];

        switch (firstError.code) {
          case "form_identifier_not_found":
            errorMessage = "No account found with this email address.";
            break;
          case "form_password_incorrect":
            errorMessage = "Incorrect password. Please try again.";
            break;
          case "form_identifier_exists":
            errorMessage = "An account with this email already exists.";
            break;
          case "form_password_pwned":
            errorMessage = "This password has been compromised. Please choose a different one.";
            break;
          case "form_password_length_too_short":
            errorMessage = "Password must be at least 6 characters long.";
            break;
          case "form_param_format_invalid":
            errorMessage = "Please enter a valid email address.";
            break;
          default:
            errorMessage = firstError.message || errorMessage;
        }
      } else {
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError("");

      const redirectUrl = `${window.location.origin}/oauth-callback`;

      if (isLogin) {
        await signIn.authenticateWithRedirect({
          strategy: "oauth_google",
          redirectUrl,
          redirectUrlComplete: redirectUrl,
        });
      } else {
        await signUp.authenticateWithRedirect({
          strategy: "oauth_google",
          redirectUrl,
          redirectUrlComplete: redirectUrl,
        });
      }
    } catch (err) {
      console.error("Google sign-in error:", err);
      let errorMessage = "Google sign-in failed. Please try again.";

      if (err.errors && err.errors.length > 0) {
        errorMessage = err.errors[0].message || errorMessage;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError("Please enter your email address first.");
      return;
    }

    if (!signInLoaded) {
      setError("Password reset not available. Please try again.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: formData.email,
      });

      setError("Password reset email sent! Check your inbox.");
    } catch (err) {
      console.error("Password reset error:", err);
      setError("Failed to send reset email. Please check your email address.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-popup" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="login-popup-content">
        <button className="close-btn" onClick={onClose}>×</button>

        <div>
          <h2>{isLogin ? "Welcome Back!" : "Create Account"}</h2>
          <p className="subtitle">
            {isLogin ? "Please login to continue" : "Register your account"}
          </p>
        </div>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit} className="form-group">
          {!isLogin && (
            <div>
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="input-field"
                placeholder="Your full name"
                required
                minLength={2}
              />
            </div>
          )}

          <div>
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="input-field"
              placeholder="••••••••"
              required
              minLength={6}
            />
            {!isLogin && (
              <small style={{ color: '#666', fontSize: '12px' }}>
                Password must be at least 6 characters long
              </small>
            )}
          </div>

          <div className="form-actions">
            <div className="remember-me">
              <input id="remember-me" name="remember-me" type="checkbox" />
              <label htmlFor="remember-me">Remember me</label>
            </div>
            {isLogin && (
              <button
                type="button"
                className="forgot-password"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Forgot password?
              </button>
            )}
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? "Processing..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        {needsVerification && (
          <div className="resend-verification">
            <p>Didn't receive the verification email?</p>
            <button
              type="button"
              className="resend-btn"
              onClick={handleResendVerification}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Resend Verification Email"}
            </button>
          </div>
        )}

        <div className="divider">
          <span>or continue with</span>
        </div>

        <div className="social-login">
          <button
            onClick={handleGoogleSignIn}
            className="social-btn google"
            disabled={isLoading || !signInLoaded}
            type="button"
          >
            <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
              <path
                d="M12.545,10.239V13.3h4.25c-.183,1.11-1.356,3.243-4.25,3.243c-2.56,0-4.646-2.119-4.646-4.731s2.086-4.731,4.646-4.731c1.411,0,2.423.595,2.973,1.134l2.035-1.957c-1.294-1.21-2.976-1.938-5.008-1.938c-4.138,0-7.5,3.362-7.5,7.5s3.362,7.5,7.5,7.5c4.318,0,7.182-3.032,7.182-7.295c0-.483-.051-.875-.12-1.257L12.545,10.239z"
                fill="#4285F4"
              />
            </svg>
            {isLoading ? "Connecting..." : "Continue with Google"}
          </button>
        </div>

        <div className="switch-form">
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
                setFormData({ email: formData.email, password: "", name: "" });
                setNeedsVerification(false);
              }}
              className="switch-btn"
              disabled={isLoading}
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}